import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { environment } from '@env';
import { NbTrigger, NbSidebarService } from '@nebular/theme';
import { resourceTypes } from '@shared/constants/resourceTypes';
import { ZSuiteHelper } from '@shared/helpers/zsuite-helper';
import { SideMenu } from '@shared/models/side-menu';
import { BrowserService } from '@shared/services/browser.service';
import { ModalService } from '@shared/services/modal.service';
import { UserService } from '@shared/modules/account/services/user.service';
import { WindowService } from '@shared/services/window.service';
import { ZSuiteService } from '@shared/services/zsuite.service';
import { Subscription } from 'rxjs';
import { AccessService } from '@access/access.service';
import { PERMISSIONS } from '@access/permissions';

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
})
export class SideMenuComponent implements OnInit, OnDestroy, AfterViewInit {
  menuItems: SideMenu[][] = [[]];
  isMobile: boolean = false;
  resourceTypes: Readonly<any> = resourceTypes;
  nbTrigger: Readonly<any> = NbTrigger;
  subscriptions: Subscription = new Subscription();
  isZSuite: boolean = false;

  constructor(
    private userService: UserService,
    private sidebarService: NbSidebarService,
    private router: Router,
    private browserService: BrowserService,
    private modalService: ModalService,
    private zSuiteService: ZSuiteService,
    private windowService: WindowService,
    private readonly accessService: AccessService
  ) {}

  ngOnInit(): void {
    this.isZSuite = ZSuiteHelper.isZSuite();

    this.isZSuite && this.sidebarService.compact('left');

    this.subscriptions.add(
      this.browserService.screenSizeChanged(900).subscribe((res) => {
        res && this.sidebarService.collapse('left');
        this.isMobile = res;
      })
    );

    this.menuItems = new Array(
      [
        new SideMenu({
          title: 'common.add-device',
          icon: 'plus-outline',
          functionName: 'onCreateModalDeviceAdd',
          disabled: !this.accessService.hasPermission(
            PERMISSIONS.Devices.Create
          ),
        }),
        new SideMenu({
          title: 'bulk.bulk-print',
          icon: 'play-circle-outline',
          functionName: 'onCreateMultiPrintModal',
          disabled: !this.accessService.hasPermission(
            PERMISSIONS.Printing.Multi
          ),
        }),
      ],
      [
        new SideMenu({
          title: 'user.my-dashboard',
          icon: 'layout-outline',
          link: '/dashboard',
          hidden: false,
        }),
        new SideMenu({
          title: 'user.statistics',
          icon: 'bar-chart-2-outline',
          link: '/statistics',
        }),
        new SideMenu({
          title: 'common.timeline',
          icon: 'menu-arrow-outline',
          link: '/timeline',
        }),
        new SideMenu({
          title: 'user.teams',
          icon: 'people-outline',
          link: '/teams',
          functionName: 'openTeamsTab',
          disabled: !this.accessService.hasPermission(
            PERMISSIONS.Teams.View
          ),
        }),
        new SideMenu({
          title: 'shelf.shelfs',
          icon: 'shelf-icon',
          link: '/shelf',
          functionName: 'openShelvesTab',
          disabled: !this.accessService.hasPermission(
            PERMISSIONS.Shelves.View
          ),
        }),
        new SideMenu({
          title: 'printouts.printouts',
          icon: 'star-outline',
          link: '/printouts',
        }),
        new SideMenu({
          title: 'multi-stream.multi-stream',
          icon: 'video-outline',
          link: '/streams',
        }),
        new SideMenu({
          title: 'subscriptions.current-plan',
          icon: 'map-outline',
          link: '/subscription',
        }),
        // new SideMenu({
        //   title: "common.vouchers",
        //   icon: "gift-outline",
        //   link: "/vouchers",
        // }),
        // new SideMenu({
        //   title: "my-files.title",
        //   icon: "folder-outline",
        //   link: "/myFiles",
        //   hidden: true
        // }),
        // new SideMenu({
        //   title: "user.settings",
        //   icon: "settings-2-outline",
        //   link: "/settings",
        //   hidden: true
        // }),
        new SideMenu({
          title: 'Roboze',
          icon: 'globe-outline',
          hidden: !this.isMobile,
          functionName: 'openRobozeMainPage',
        }),
      ],
      [
        new SideMenu({
          title: 'user.admin-panel',
          icon: 'shield-outline',
          link: '/admin',
          hidden: !this.isAdmin(),
        }),
        new SideMenu({
          title: 'common.report-problem',
          icon: 'alert-triangle-outline',
          hidden: this.isZSuite,
          functionName: 'createModalReportProblem',
        }),
        new SideMenu({
          title: 'user.log-out',
          icon: 'log-out-outline',
          hidden: this.isZSuite,
          functionName: 'logout',
        }),
      ]
    );

    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        this.onClose();
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.checkPolicyAndVersionDelivered();
    }, 2000);
  }

  isActiveRoute(url: string): boolean {
    if (!url) {
      return false;
    }
    return this.router.isActive(url, false);
  }

  ngOnDestroy() {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  toggleMenu() {
    this.sidebarService.toggle(false, 'left');
  }

  isAdmin(): boolean {
    return this.accessService.hasPermission(PERMISSIONS.Admin.Dashboard.View);
  }

  onCreateModalDeviceAdd(): void {
    if (!this.accessService.hasPermission(PERMISSIONS.Devices.Create)) {
      return;
    }

    this.modalService.showModalDeviceAdd();
  }

  onCreateMultiPrintModal(): void {
    this.modalService.showMultiPrintModal();
  }

  createModalReportProblem(): void {
    this.modalService.showReportProblemModal();
  }

  openTeamsTab(): void {
    if (this.accessService.hasPermission(PERMISSIONS.Teams.View)) {
      this.router.navigate(['/teams']);
    } else {
      this.modalService.showUnavailableTeamsModal();
    }
  }

  openShelvesTab(): void {
    if (this.accessService.hasPermission(PERMISSIONS.Shelves.View)) {
      this.router.navigate(['/shelf']);
    } else {
      this.modalService.showUnavailableShelvesModal();
    }
  }

  logout() {
    if (this.isZSuite) {
      this.zSuiteService.logout();
    } else {
      this.userService.logout();
    }
  }

  checkPolicyAndVersionDelivered() {
    const user = this.userService.user;

    if (user != null) {
      this.modalService
        .showPrivacyPolicyModal(!user.privacyPolicyDelivered)
        .then(() => {
          this.modalService.showNewVersionAvailableModal(
            !user.newVersionDelivered
          );
        });
    }
  }

  trackByFn(index: number, item: SideMenu | SideMenu[]) {
    return item;
  }

  onClose() {
    this.isMobile && this.sidebarService.collapse('left');
  }

  openRobozeMainPage() {
    this.windowService.blankOpen(environment.roboze.url);
  }
}
